import {
  collection, getDocs, query, orderBy, limit, where, Timestamp
} from "firebase/firestore";
import { db } from "../config/firebase";

// News functions and helpers
const getNewsCacheKey = (days, pageSize) => `news_cache_${days}_${pageSize}`;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

const isCacheValid = (timestamp) => {
  return (Date.now() - timestamp) < CACHE_DURATION;
};

const sanitizeNewsText = (value) => String(value || '').trim();

const isMissingNewsTitle = (value) => {
  const normalized = sanitizeNewsText(value).toUpperCase();
  return !normalized || normalized === 'N/A' || normalized === 'NA';
};

const deriveNewsTitle = (data) => {
  const rawTitle = data.Title || data.title || data.headline || '';
  if (!isMissingNewsTitle(rawTitle)) {
    return sanitizeNewsText(rawTitle);
  }

  const description = sanitizeNewsText(data.Description || data.description || '');
  if (description) {
    return description.length > 110 ? `${description.slice(0, 107).trimEnd()}...` : description;
  }

  return 'Actualite sans titre';
};

export const loadNewsArticles = async (days = 30, useCache = true, pageSize = 50) => {
  try {
    const cacheKey = getNewsCacheKey(days, pageSize);
    let newsData = [];
    if (useCache) {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          newsData = parsedData.newsData;
          const cacheTimestamp = parsedData.timestamp;

          if (isCacheValid(cacheTimestamp)) {
            newsData = newsData.map(article => ({
              ...article,
              createdAt: new Date(article.createdAt),
              lastUpdated: new Date(article.lastUpdated),
              pubDate: article.pubDate ? new Date(article.pubDate) : null
            }));
            return newsData;
          }
        }
      } catch {
        // localStorage unavailable (iOS Safari private mode / quota exceeded)
      }
    }

    // Calculate the date for filtering (last X days)
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - days);
    const daysAgoTimestamp = Timestamp.fromDate(daysAgo);

    let newsQuery = query(
      collection(db, "news"),
      where("createdAt", ">=", daysAgoTimestamp),
      orderBy("createdAt", "desc"),
      limit(pageSize)
    );

    const newsSnapshot = await getDocs(newsQuery);
    const newNewsData = newsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        Source: data.Source || '',
        Title: deriveNewsTitle(data),
        Link: data.Link || '',
        Description: data.Description || '',
        'Publication Date': data['Publication Date'] || '',
        Categories: Array.isArray(data.Categories) ? data.Categories : [],
        mediaType: data.mediaType || 'article',
        createdAt: data.createdAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        pubDate: data['Publication Date'] ? new Date(data['Publication Date']) : null
      };
    });

    // Update cache
    if (useCache) {
      try {
        const cacheData = {
          newsData: newNewsData.map(article => ({
            ...article,
            createdAt: article.createdAt.toISOString(),
            lastUpdated: article.lastUpdated.toISOString(),
            pubDate: article.pubDate ? article.pubDate.toISOString() : null
          })),
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch {
        // localStorage write failed (iOS Safari private mode / quota exceeded)
      }
    }

    return newNewsData;
  } catch (error) {
    console.error("Error loading news articles:", error);
    throw error;
  }
};

export const loadNewsBySource = async (source, days = 30) => {
  try {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - days);
    const daysAgoTimestamp = Timestamp.fromDate(daysAgo);

    let newsQuery = query(
      collection(db, "news"),
      where("Source", "==", source),
      where("createdAt", ">=", daysAgoTimestamp),
      orderBy("createdAt", "desc")
    );

    const newsSnapshot = await getDocs(newsQuery);
    return newsSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        Source: data.Source || '',
        Title: deriveNewsTitle(data),
        Link: data.Link || '',
        Description: data.Description || '',
        'Publication Date': data['Publication Date'] || '',
        Categories: Array.isArray(data.Categories) ? data.Categories : [],
        mediaType: data.mediaType || 'article',
        createdAt: data.createdAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        pubDate: data['Publication Date'] ? new Date(data['Publication Date']) : null
      };
    });
  } catch (error) {
    console.error("Error loading news by source:", error);
    throw error;
  }
};

export const getNewsSources = async () => {
  try {
    const newsQuery = query(collection(db, "news"));
    const newsSnapshot = await getDocs(newsQuery);
    
    const sources = new Set();
    newsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.Source) {
        sources.add(data.Source);
      }
    });
    
    return Array.from(sources).sort();
  } catch (error) {
    console.error("Error getting news sources:", error);
    throw error;
  }
};

export const clearNewsCache = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith("news_cache_"))
    .forEach((key) => localStorage.removeItem(key));
};
